import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import { CompositeScreenProps } from "@react-navigation/native"
import { observer } from "mobx-react-lite"
import { Appearance, ImageStyle, TextInput, TextStyle, TouchableOpacity, TouchableOpacityProps, ViewStyle } from "react-native"
import {
  ModelCreateScreen,
  PondProfileForm as ModelForm,
  Button,
  Text,
  AutoImage,
  FormControl,
  FormControlProps,
} from "../../../../components"
import { Pond } from "../../../../database"
import SyncableModel from "../../../../database/SyncableModel"
import { useStores } from "../../../../models"
import JalaAnalytic from "../../../../utils/jala-analytic"
import { goBack, navigate } from "../../../../navigators"
import _ from "lodash"
import { Modal, View, useDisclose } from "native-base"
import { color, nativeBaseTheme } from "../../../../theme"
import { translate } from "../../../../i18n"
import { FastField, Formik, FormikProps, FormikValues } from "formik"
import * as yup from "yup"

const IMAGE : ImageStyle = {
  resizeMode: "contain",
  height: 120,
  width: 150
}

const BUTTON_LEFT = (isValid): ViewStyle => ({
  alignSelf: "center",
  paddingVertical: 6,
  paddingHorizontal: 16,
  borderRadius: 38,
  backgroundColor: color.palette.white,
  borderColor: isValid ? color.primary : color.palette.gray500
})

const BUTTON_RIGHT: ViewStyle = {
  alignSelf: "center",
  paddingVertical: 6,
  paddingHorizontal: 16,
  borderRadius: 38,
}

const boldStyle: TextStyle = {
  fontWeight: 'bold'
}

const TextField: FC<{colorScheme?: "light" | "dark", formControlProps?: FormControlProps}> = observer(function TextField(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), formControlProps } = props

  const input = { name: React.useRef<TextInput>() }

  return (
    <FastField name="name">
      {({ field, form: { setFieldValue, setFieldTouched, handleBlur }, meta }) => (
        <FormControl
          isRequired
          isInvalid={meta.touched && !!meta.error}
          colorScheme={colorScheme}
          inputProps={{
            testID: 'jala:id/delete-pond-name-input',
            ref: input.name,
            autoCapitalize: "none",
            autoComplete: "off",
            autoCorrect: false,
            textContentType: "none",
            keyboardType: "default",
            placeholderTx: "pondProfileForm.pondNamePlaceholder",
            defaultValue: meta.initialValue,
            onChangeText: (value) => {
              setFieldTouched(field.name)
              setFieldValue(field.name, value)
            },
            onBlur: handleBlur(field.name),
            // onSubmitEditing: () => isValid && handleSubmit(),
          }}
          errorMessage={meta.error}
          {...formControlProps}
        />
        )}
    </FastField>
  )
})


const DeleteModal: FC< {
  colorScheme?: "light" | "dark",
  innerRef?: React.Ref<FormikProps<FormikValues>> | ((node) => void),
  initialValue?: any,
  onConfirm?: () => void
} & Omit<TouchableOpacityProps, "onPress">> = observer(function DeleteModal(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    children,
    innerRef,
    initialValue,
    onConfirm: onConfirmProp,
    ...touchableOpacityProps
  } = props;
  const { isOpen, onClose, onOpen } = useDisclose()
  const image = require("./delete-pond.png")

  const validationSchema = useMemo(() => yup.object({
    name: yup.string().ascii()
      .oneOf([yup.ref('pond_name')], translate("pondProfileForm.modalErrorMessage", { pond: initialValue.pond_name }))
  }), [initialValue.pond_name])

  const formatBoldText = (pondName) => {
    // const parts = text.split('<bold>');
    return (
      <Text color={color.palette.gray800}>
        {translate("pondProfileForm.deleteModalSubtitle")}
        <Text color={color.palette.gray800} fontWeight="bold">{`"${pondName}" `}</Text>
        {translate("pondProfileForm.deleteModalSubtitleLast")}
      </Text>
    );
  };

  const onConfirm = useCallback(() => {
    onConfirmProp && onConfirmProp()
    onClose()
  }, [onClose, onConfirmProp])

  return (<>
    <TouchableOpacity onPress={onOpen} {...touchableOpacityProps}>
      {children}
    </TouchableOpacity>
    <Modal isOpen={isOpen} onClose={onClose}>
      <View
        alignSelf="stretch"
        marginY="80px"
        marginX="28px"
        paddingY="24px"
        paddingX="16px"
        rounded="16px"
        backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
      >
        <View alignItems="center" marginBottom="12px">
          <AutoImage source={image} style={IMAGE} />
          <Text
            tx="pondProfileForm.deleteModalTitle"
            preset="display3"
            colorScheme={colorScheme}
            color={color.palette.gray800}
            marginBottom="12px"
            marginTop="8px"
            textAlign="center"
          />
          <Text
            preset="body2"
            colorScheme={colorScheme}
            marginBottom="20px"
            color={color.palette.gray800}
            textAlign="justify"
          >
            {formatBoldText(initialValue.pond_name)}
          </Text>
          <Formik
            initialValues={{ ...initialValue }}
            validationSchema={validationSchema}
            onSubmit={onConfirm}
            enableReinitialize
            innerRef={innerRef}
          >
          {({ isValid, values }) => (
            <>
              <TextField colorScheme={colorScheme} />
              <View flexDirection="row" justifyContent="center" marginTop="25px">
                <Button
                  testID="jala:id/delete-yes-button"
                  tx="pondProfileForm.confirmDeleteButton"
                  preset="outline"
                  textPreset="title4"
                  style={BUTTON_LEFT(values.name !== "" && isValid)}
                  onPress={onConfirm}
                  disabled={ values.name === "" || !isValid}
                  textColor={values.name !== "" && isValid ? color.primary : color.palette.gray500}
                />
                <View width="12px" />
                <Button
                  testID="jala:id/delete-no-button"
                  tx="pondProfileForm.cancelDeleteButton"
                  preset="primary"
                  textPreset="title4"
                  colorScheme={colorScheme}
                  style={BUTTON_RIGHT}
                  onPress={onClose}
                />
              </View>
            </>
          )}
          </Formik>
        </View>
      </View>
    </Modal>
  </>)
})

export const PondProfileScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function PondProfileScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), route: { params = {} } } = props

  const [ model, setModel ] = useState<SyncableModel>(null);

  // Pull in one of our MST stores
  const { appStore: { farmId, farmStore },
    pondStore: modelStore
  } = useStores();

  const initialValues = useMemo<any>(() => {
    return {
      farm_id: farmId,
      ...(model ? model.toArray() : params),
      rfid: params.rfid
    }
  }, [farmId, model, params])

  useEffect(() => {
    modelStore.init();
  }, [modelStore])

  useEffect(() => {
    const pond = farmStore.ponds.find((m) => m.id === params.id);
    if (pond) {
      const attributes = _.omit(pond, ['pro', 'rfid', 'rfidables', 'area']);
      const model = Pond.hydrate({ ...attributes })
      setModel(model);
      JalaAnalytic.log('Mobile: Visit Setting Pond');
    }
  },[farmStore, params, setModel])

  const onSuccess = useCallback((success: boolean, model: SyncableModel, state) => {
    if (modelStore.needRefresh) {
      if (success) {
        farmStore.initWithLastBatch(() => {
          modelStore.set('needRefresh', false);
          modelStore.set('pushIdQueue', {})
        });
      } else {
        modelStore.set('needRefresh', false);
        modelStore.set('pushIdQueue', {})
      }
    }

    if (state === 'delete') {
      navigate('Home.Stack', 'Home.Farm.Pond.List')
      return false; // Navigate handled manually
    }
    return success
  }, [farmStore, modelStore])

  const handleDelete = useCallback(async () => {
    if (!modelStore) return;
    modelStore.set('loading', true)
    const success = await modelStore.delete(model)

    JalaAnalytic.log("Mobile app: Pond Delete", {
      form: ModelForm.displayName || 'Unknown',
      success
    })

    if ((!onSuccess || onSuccess(success, model, 'delete')) && success) {
      goBack('Home.Stack')
    }
    modelStore.set('loading', false)
  }, [model, modelStore, onSuccess])

  const DeleteButton = useCallback(() => {
    const pond = farmStore.ponds.find((m) => m.id === params.id);
    return (
    model && <DeleteModal
      testID="jala:id/delete-button"
      initialValue={{name: '', pond_name: `${translate("autoFeederPondForm.pondId")} ${pond.name}` }}
      onConfirm={handleDelete}
    >
      <Text
        tx="delete"
        preset="title4"
        colorScheme={colorScheme}
        color={nativeBaseTheme.colors.danger["500"]}
        textAlign="right"
        marginRight="16px"
        marginY="6px"
      />
    </DeleteModal>
  )}, [colorScheme, farmStore.ponds, handleDelete, model, params.id])

  return (
      <ModelCreateScreen
        colorScheme={colorScheme}
        ModelForm={ModelForm}
        initialValues={initialValues}
        modelStore={modelStore}
        model={model}
        onSuccess={onSuccess}
        renderDeleteButton={DeleteButton}
      />
  )
})
